import { A11yModule } from '@angular/cdk/a11y';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import {
    AvatarComponent,
    MenuDecoratorDirective,
    MenuDialogComponent,
    UserComponent,
} from '@interacta-shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
    UserMenuLanguage,
    UserMenuPage,
    UserMenuTheme,
    UserMenuThemeOption,
    UserMenuUser,
} from '../../models/user-menu.model';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { UserMenuButtonComponent } from './user-menu-button/user-menu-button.component';
import { UserMenuLanguageComponent } from './user-menu-language/user-menu-language.component';
import { UserMenuSeparatorComponent } from './user-menu-separator/user-menu-separator.component';
import { UserMenuThemeComponent } from './user-menu-theme/user-menu-theme.component';

@Component({
    selector: 'interacta-user-menu',
    templateUrl: './user-menu.component.html',
    imports: [
        A11yModule,
        TranslatePipe,
        UserComponent,
        UserMenuButtonComponent,
        UserMenuLanguageComponent,
        UserMenuSeparatorComponent,
        UserMenuThemeComponent,
        AvatarComponent,
        MenuDialogComponent,
        MenuDecoratorDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMenuComponent {
    private readonly confirmDialogService = inject(ConfirmDialogService);

    readonly origin = input.required<CdkOverlayOrigin>();
    readonly currentUser = input.required<UserMenuUser>();
    readonly currentTheme = input.required<UserMenuTheme>();
    readonly isOpen = input(false);

    readonly availableLanguages = input<UserMenuLanguage[]>([]);
    readonly currentLanguage = input<UserMenuLanguage | null>(null);
    readonly userSubtitle = input<string | null>(null);

    readonly closing = output<void>();
    readonly logout = output<void>();
    readonly changeTheme = output<UserMenuThemeOption>();
    readonly changeLanguage = output<UserMenuLanguage>();

    readonly currentPage = signal<UserMenuPage>('home');
    readonly overridePositions = signal<ConnectedPosition[]>([
        {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'top',
        },
    ]);

    readonly themeLabel = computed(() =>
        this.currentTheme().isSystem
            ? 'SHARED.USER_MENU.THEME_DEFAULT'
            : this.currentTheme().mode === 'light'
              ? 'SHARED.USER_MENU.THEME_LIGHT'
              : 'SHARED.USER_MENU.THEME_DARK',
    );
    readonly themeIcon = computed(() =>
        this.currentTheme().isSystem
            ? 'half-sun'
            : this.currentTheme().mode === 'light'
              ? 'sun'
              : 'moon',
    );

    onClosing(): void {
        this.closing.emit();
        this.currentPage.set('home');
    }

    async onLogout(): Promise<void> {
        const confirmed = await firstValueFrom(
            this.confirmDialogService.open({
                title: 'SHARED.USER_MENU.LOGOUT_CONFIRMATION',
                cancel: 'UI.BUTTON.CANCEL',
                confirm: 'SHARED.LOGOUT',
            }),
        );
        if (confirmed) this.logout.emit();
    }
}
